import type { MetaWatchlistDefinition } from '../models/definition.types'
import type {
  MetaDraftErrors,
  MetaDraftValue,
  MetaWatchlistDraft,
  WatchlistFormField,
} from '../models/watchlist.types'

export const META_MAX_CONFIGS = 10
export const META_PAGE_ID = /^\d{8,}$/u

function cloneValue(value: unknown): MetaDraftValue {
  if (Array.isArray(value)) return value.map(String)
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  if (value && typeof value === 'object') {
    const range = value as { min?: unknown; max?: unknown }
    return {
      start: typeof range.min === 'string' ? range.min : null,
      end: typeof range.max === 'string' ? range.max : null,
    }
  }
  return null
}

export function emptyMetaValues(
  fields: readonly WatchlistFormField[],
): Record<string, MetaDraftValue> {
  const values: Record<string, MetaDraftValue> = {}
  for (const field of fields) {
    if (field.key === 'ad_reached_countries') {
      values[field.key] = (field.options ?? []).map((option) => option.value)
    } else if (field.key === 'search_page_ids') values[field.key] = ['']
    else if (field.ui_component === 'daterange') values[field.key] = { start: null, end: null }
    else if (field.type === 'array') values[field.key] = []
    else if (field.ui_component === 'toggle') values[field.key] = false
    else values[field.key] = null
  }
  return values
}

export function setForm(
  definition: MetaWatchlistDefinition | null,
  fields: readonly WatchlistFormField[],
  uid: string,
): MetaWatchlistDraft {
  const values = emptyMetaValues(fields)
  if (definition) {
    const source: Record<string, unknown> = {
      ...definition.params,
      config_name: definition.config_name,
    }
    for (const field of fields) {
      const raw = source[field.key]
      if (field.ui_component === 'multiselect' && raw === 'all') {
        values[field.key] = (field.options ?? []).map((option) => option.value)
      } else if (raw !== undefined) values[field.key] = cloneValue(raw)
    }
  }
  return { uid, values, touched: {} }
}

function hasValue(value: MetaDraftValue | undefined): boolean {
  if (Array.isArray(value)) return value.some((item) => item.trim().length > 0)
  if (value && typeof value === 'object') return Boolean(value.start || value.end)
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  return value !== null && value !== ''
}

export function validateMetaDraft(draft: MetaWatchlistDraft): MetaDraftErrors {
  const errors: MetaDraftErrors = {}
  const name = String(draft.values.config_name ?? '').trim()
  const pageIds = draft.values.search_page_ids
  const pageId = Array.isArray(pageIds) ? String(pageIds[0] ?? '').trim() : ''
  if (!name) errors.config_name = 'requiredName'
  if (!pageId) errors.page_id = 'requiredPageId'
  else if (!META_PAGE_ID.test(pageId)) errors.page_id = 'pageIdFormat'
  return errors
}

export function buildPayload(
  drafts: readonly MetaWatchlistDraft[],
  fields: readonly WatchlistFormField[],
): { payload: MetaWatchlistDefinition[]; errors: Record<string, MetaDraftErrors> } {
  const errors: Record<string, MetaDraftErrors> = {}
  const payload = drafts.map((draft) => {
    const draftErrors = validateMetaDraft(draft)
    if (Object.keys(draftErrors).length) errors[draft.uid] = draftErrors
    const params: Record<string, unknown> = {}
    for (const field of fields) {
      const value = draft.values[field.key]
      if (!hasValue(value)) continue
      if (
        field.ui_component === 'daterange' &&
        value &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        params[field.key] = { min: value.start, max: value.end }
      } else if (
        field.key === 'ad_reached_countries' &&
        Array.isArray(value) &&
        value.length === (field.options ?? []).length
      ) {
        params[field.key] = 'all'
      } else {
        params[field.key] = Array.isArray(value) ? value.filter(Boolean) : value
      }
    }
    const configName = String(draft.values.config_name ?? '').trim()
    params.config_name = configName
    return { config_name: configName, params }
  })
  return { payload, errors }
}

export function metaFilterFields(fields: readonly WatchlistFormField[]): WatchlistFormField[] {
  return fields.filter(
    (field) => !['search_terms', 'search_page_ids', 'config_name'].includes(field.key),
  )
}
